import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { MapPin, Phone, Mail, Globe, ArrowLeft, BadgeCheck, X, Clock, Map, Linkedin, Facebook, Instagram } from 'lucide-react';
import Footer from '../components/Footer';
import { getMember } from '../lib/api';

export default function MemberDetailPage() {
  const { id } = useParams();
  const [m, setM] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    getMember(id).then(setM).catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#f8f6f2] px-6">
        <p className="font-serif-ah text-[32px] text-[#555] mb-4">Member not found.</p>
        <Link to="/directory" className="btn-ah-solid font-sans-ah text-[13px] px-8 py-3.5 rounded-sm">BACK TO DIRECTORY</Link>
      </div>
    );
  }

  if (!m) {
    return <div className="min-h-screen bg-[#f8f6f2] animate-pulse" />;
  }

  const contactRows = [
    m.phone && { icon: Phone, label: m.phone, href: `tel:${m.phone}` },
    m.email && { icon: Mail, label: m.email, href: `mailto:${m.email}` },
    m.website && { icon: Globe, label: m.website.replace(/^https?:\/\//, ''), href: m.website },
    (m.address || m.city) && { icon: MapPin, label: [m.address, m.city, m.postal_code].filter(Boolean).join(', ') },
  ].filter(Boolean);

  return (
    <>
      <section className="relative pt-32 pb-16 bg-[#1f2c33] overflow-hidden">
        <div className="absolute inset-0 opacity-25 bg-cover bg-center" style={{ backgroundImage: `url(${m.image || 'https://images.unsplash.com/photo-1533061968564-f84e14bb5501?w=1600&q=80'})` }} />
        <div className="relative max-w-[1000px] mx-auto px-6 lg:px-10">
          <Link to="/directory" className="inline-flex items-center gap-2 font-sans-ah text-[12px] tracking-wide-ah text-white/70 hover:text-white mb-6">
            <ArrowLeft size={15} /> BACK TO DIRECTORY
          </Link>
          <div className="flex items-center gap-2 mb-3">
            {m.status === 'Active' && (
              <span className="inline-flex items-center gap-1.5 font-sans-ah text-[11px] tracking-wide-ah text-[#8fc0e2]">
                <BadgeCheck size={15} /> ACTIVE MEMBER{m.member_since ? ` SINCE ${new Date(m.member_since).getFullYear()}` : ''}
              </span>
            )}
          </div>
          <h1 className="font-serif-ah text-white text-[42px] md:text-[58px] leading-none">{m.name}</h1>
          <div className="flex flex-wrap gap-2 mt-5">
            {(m.categories || []).map((c) => (
              <span key={c} className="font-sans-ah text-[11px] tracking-wide-ah uppercase text-white bg-white/10 px-3 py-1.5 rounded-sm">{c}</span>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#f8f6f2] py-16 md:py-20">
        <div className="max-w-[1000px] mx-auto px-6 lg:px-10 grid md:grid-cols-3 gap-10">
          <div className="md:col-span-2">
            <h2 className="font-serif-ah text-[30px] text-[#2f2f2f] mb-5">About {m.name}</h2>
            <p className="font-sans-ah text-[16px] text-[#454545] font-300 leading-[1.9] whitespace-pre-line">
              {m.description || 'A proud member of the Almaguin Highlands Chamber of Commerce.'}
            </p>

            {(m.gallery || []).length > 0 && (
              <div className="mt-10" data-testid="member-gallery">
                <h3 className="font-sans-ah tracking-nav text-[12px] text-[#4a9cd4] mb-5">PHOTO GALLERY</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {m.gallery.map((url, i) => (
                    <button
                      key={url}
                      onClick={() => setLightbox(url)}
                      data-testid={`gallery-thumb-${i}`}
                      className="group relative aspect-[4/3] overflow-hidden rounded-sm bg-neutral-100"
                    >
                      <img src={url} alt={`${m.name} photo ${i + 1}`} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                      <span className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div>
            <div className="bg-white rounded-sm p-7 border border-neutral-100 shadow-[0_6px_30px_rgba(0,0,0,0.05)]">
              <h3 className="font-sans-ah tracking-nav text-[12px] text-[#4a9cd4] mb-5">CONTACT INFORMATION</h3>
              <ul className="space-y-4">
                {contactRows.map((r) => (
                  <li key={r.label || r.value} className="flex items-start gap-3">
                    <r.icon size={17} className="text-[#4a9cd4] shrink-0 mt-0.5" />
                    {r.href ? (
                      <a href={r.href} target="_blank" rel="noreferrer" className="font-sans-ah text-[14px] text-[#444] hover:text-[#4a9cd4] break-words">{r.label}</a>
                    ) : (
                      <span className="font-sans-ah text-[14px] text-[#444]">{r.label}</span>
                    )}
                  </li>
                ))}
              </ul>
              {m.contact_name && (
                <p className="font-sans-ah text-[13px] text-[#888] mt-6 pt-5 border-t border-neutral-100">
                  Contact: <span className="text-[#444]">{m.contact_name}</span>
                </p>
              )}
              {m.service_area && (
                <div className="mt-5 pt-5 border-t border-neutral-100" data-testid="member-service-area">
                  <p className="font-sans-ah text-[11px] tracking-wide-ah text-[#999] uppercase mb-2 flex items-center gap-2"><Map size={14} className="text-[#4a9cd4]" /> Service Area</p>
                  <p className="font-sans-ah text-[14px] text-[#444] font-300">{m.service_area}</p>
                </div>
              )}
              {m.business_hours && (
                <div className="mt-5 pt-5 border-t border-neutral-100" data-testid="member-business-hours">
                  <p className="font-sans-ah text-[11px] tracking-wide-ah text-[#999] uppercase mb-2 flex items-center gap-2"><Clock size={14} className="text-[#4a9cd4]" /> Business Hours</p>
                  <p className="font-sans-ah text-[14px] text-[#444] font-300 whitespace-pre-line leading-relaxed">{m.business_hours}</p>
                </div>
              )}
              {(m.linkedin || m.facebook || m.instagram) && (
                <div className="mt-5 pt-5 border-t border-neutral-100" data-testid="member-socials">
                  <p className="font-sans-ah text-[11px] tracking-wide-ah text-[#999] uppercase mb-3">Follow</p>
                  <div className="flex gap-3">
                    {m.facebook && (
                      <a href={m.facebook} target="_blank" rel="noreferrer" aria-label="Facebook" data-testid="social-facebook" className="w-9 h-9 rounded-full border border-neutral-200 flex items-center justify-center text-[#4a9cd4] hover:bg-[#4a9cd4] hover:text-white hover:border-[#4a9cd4] transition-colors"><Facebook size={16} /></a>
                    )}
                    {m.instagram && (
                      <a href={m.instagram} target="_blank" rel="noreferrer" aria-label="Instagram" data-testid="social-instagram" className="w-9 h-9 rounded-full border border-neutral-200 flex items-center justify-center text-[#4a9cd4] hover:bg-[#4a9cd4] hover:text-white hover:border-[#4a9cd4] transition-colors"><Instagram size={16} /></a>
                    )}
                    {m.linkedin && (
                      <a href={m.linkedin} target="_blank" rel="noreferrer" aria-label="LinkedIn" data-testid="social-linkedin" className="w-9 h-9 rounded-full border border-neutral-200 flex items-center justify-center text-[#4a9cd4] hover:bg-[#4a9cd4] hover:text-white hover:border-[#4a9cd4] transition-colors"><Linkedin size={16} /></a>
                    )}
                  </div>
                </div>
              )}
            </div>
            <Link to="/membership" className="block text-center btn-ah-outline-dark font-sans-ah text-[13px] px-6 py-4 rounded-sm mt-5">
              BECOME A MEMBER
            </Link>
          </div>
        </div>
      </section>
      {lightbox && (
        <div
          className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center p-6"
          onClick={() => setLightbox(null)}
          data-testid="gallery-lightbox"
        >
          <button aria-label="Close" className="absolute top-6 right-6 w-11 h-11 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 transition-colors">
            <X size={22} />
          </button>
          <img src={lightbox} alt="Gallery" className="max-w-full max-h-[85vh] object-contain rounded-sm shadow-2xl" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
      <Footer />
    </>
  );
}
